<template>
  <AkDrawer
    :is-open="opened"
    :overlay="false"
    :z-index="1030"
    right
    :width="fullWidth ? '100%' : '40%'"
    class="filters-drawer"
    @update:open="open"
    @update:close="close"
  >
    <div class="filters-drawer__main">
      <AkHeading4 class="ds-mb-2">
        {{ $t('brands.contactManagement.filtersDrawer.title') }}
      </AkHeading4>
      <AkParagraph size="sm">
        {{ $t('brands.contactManagement.filtersDrawer.subTitle') }}
      </AkParagraph>
      <div class="ds-mt-5">
        <ActiveFilters />
      </div>
      <template
        v-for="(group, index) in getGroupedFilters"
        :key="index"
      >
        <div
          class="ds-flex ds-justify-between ds-border-t ds-border-solid ds-border-neutral-300 ds-mt-5 ds-pt-5"
          @click="openFilterGroup(index)"
        >
          <AkHeading4
            class="filter-group-name"
            data-testid="filter-group-name"
          >
            {{ getGroupNameTranslation(group) }}
          </AkHeading4>
          <AkIcon
            :symbol="showGroup[index] ? 'chevron-up' : 'chevron-down'"
            size="md"
            data-testid="filter-group-toggle"
            class="ds-cursor-pointer"
          />
        </div>
        <AkExpander
          :keep-alive="true"
          :open="showGroup[index]"
        >
          <template
            v-for="(item, itemIndex) in group.items"
            :key="itemIndex"
          >
            <AkParagraph
              v-if="item.groupName"
              class="ds-mt-5 ds-text-neutral-900"
              data-testid="filter-subgroup-name"
            >
              {{ getGroupNameTranslation(item) }}
            </AkParagraph>
            <template
              v-for="(comp, compIndex) in item.components"
              :key="compIndex"
            >
              <div
                v-if="comp.values || comp.attributes || comp.type === WidgetTypes.ToggleWidget"
                class="filters-drawer__section"
              >
                <component
                  :is="resolveWidgetComponent(comp)"
                  :ref="comp.type"
                  :filter="comp"
                  :model-value="selectedFilters[comp.attributeName]"
                  @update:model-value="setSelectedFilter(comp.attributeName, $event)"
                />
              </div>
            </template>
          </template>
        </AkExpander>
      </template>
    </div>
    <div class="filters-drawer__bottom">
      <AkButton
        size="md"
        @click="submitFilters"
      >
        {{ $t('brands.contactManagement.filtersDrawer.apply') }}
      </AkButton>
    </div>
  </AkDrawer>
</template>

<script lang="ts">
import { defineComponent, ref, computed, type ComputedRef, type Component } from 'vue';
import { useStore } from '@core/composables/use-store';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';
import { debounce } from '@core/utilities/lodash-utils';
import { AkParagraph, AkHeading4, AkButton, AkDrawer, AkExpander } from '@ankorstore/design-system';
import { type ContactFilter, ContactFilterType } from '../../types/api.types';
import ActiveFilters from '../active-filters/active-filters.vue';
import RangeWidget from './range-widget.vue';
import DropdownWidget from './dropdown-widget.vue';
import MultiSelectWidget from './multi-select-widget.vue';
import ListWidget from './list-widget.vue';
import ToggleWidget from './toggle-widget.vue';
import DateRangeWidget from './date-range-widget.vue';
import RetailerOriginWidget from './retailer-origin-widget.vue';
import Analytics from '@monolith/legacy/services/analytics';
import { BrandClickedEvent } from '@monolith/legacy/services/analytics/events/brand-clicked.event';
import { BrandClickAction } from '@monolith/legacy/types/analytics/brand-click-event';
import { WidgetTypes } from '../../types/filter.types';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'FiltersDrawer',
  components: {
    RangeWidget,
    DropdownWidget,
    MultiSelectWidget,
    ListWidget,
    ToggleWidget,
    RetailerOriginWidget,
    ActiveFilters,
    AkParagraph,
    AkHeading4,
    AkButton,
    AkDrawer,
    AkExpander,
  },
  setup() {
    const { isMobile } = useBreakpoints();
    const store = useStore();
    const { t } = useI18n();
    const opened = ref(false);
    const showGroup = ref([true, true, true]);
    const getGroupedFilters = computed(() => store.getters['account/contactManagement/getGroupedFilters']);

    const fullWidth = computed(() => isMobile.value);
    const selectedFilters = computed(() => store.getters['account/contactManagement/getFiltersState']);
    const selectedFiltersStateForAnalytics = computed(
      () => store.getters['account/contactManagement/getFiltersStateForAnalytics']
    );
    const brandId: ComputedRef<number> = computed(() => store.getters['brand']?.id);
    const DEBOUNCE_TIME = 350;
    const touchedFilters = new Map<string, ContactFilter | null>();

    const close = () => {
      // Emit one per-filter `apply_filter` event for every filter still active at close, so each filter
      // is counted once per session (a multi-select counts once) with its final value.
      touchedFilters.forEach((value, id) => {
        if (value) {
          trackFilterApplied(id, value);
        }
      });
      touchedFilters.clear();
      opened.value = false;
    };

    const returnComponent = (component: WidgetTypes): Component | null => {
      switch (component) {
        case WidgetTypes.RangeWidget:
          return RangeWidget;
        case WidgetTypes.DropdownWidget:
          return DropdownWidget;
        case WidgetTypes.MultiSelectWidget:
          return MultiSelectWidget;
        case WidgetTypes.ListWidget:
          return ListWidget;
        case WidgetTypes.ToggleWidget:
          return ToggleWidget;
        case WidgetTypes.DateRangeWidget:
          return DateRangeWidget;
      }
      return null;
    };

    // The "Retailer origin" filter (contact.source) is a server-driven ListWidget but renders as a
    // searchable channel tree with an expandable Events group, so it gets a dedicated component.
    const resolveWidgetComponent = (comp: { attributeName?: string; type: WidgetTypes }): Component | null => {
      if (comp.attributeName === 'contact.source') {
        return RetailerOriginWidget;
      }
      return returnComponent(comp.type);
    };

    const open = () => {
      touchedFilters.clear();
      opened.value = true;
    };

    // Emitted once per individual filter (id = the filter name, e.g. `retailer.shopType`) when the drawer
    // closes, so each filter is an analyzable metric in Amplitude and is counted once per session with its
    // final value (a multi-select with several value changes counts once). The bundled `apply_filters`
    // event in `updateFilter` is kept untouched for the existing dashboards that rely on the full state.
    const trackFilterApplied = (id: string, value: ContactFilter) => {
      Analytics.track(
        new BrandClickedEvent({
          brand_id: brandId.value,
          action: BrandClickAction.BrandApplyFilter,
          clicked_value: id,
          value,
        })
      );
    };

    const setSelectedFilter = (id: string, value: ContactFilter) => {
      const isEmptyRange = value.type === ContactFilterType.Range && !value.min && !value.exclusiveMax;
      const isEmptyValue = value.type === ContactFilterType.Value && !value.value?.length;
      const isEmptyBoolean = value.type === ContactFilterType.Boolean && value.value === undefined;
      const effectiveValue = isEmptyRange || isEmptyValue || isEmptyBoolean ? null : value;

      // Record the latest value for this filter this session; the `apply_filter` event is emitted once
      // per active filter when the drawer closes (see `close`), so intermediate values don't over-count.
      touchedFilters.set(id, effectiveValue);
      updateFilter(id, effectiveValue);
    };

    const updateFilter = debounce(async (id, value) => {
      await store.dispatch('account/contactManagement/setFilter', [id, value]);
      Analytics.track(
        new BrandClickedEvent({
          brand_id: brandId.value,
          action: BrandClickAction.BrandApplyFilters,
          value: selectedFiltersStateForAnalytics.value,
        })
      );
    }, DEBOUNCE_TIME);

    const submitFilters = async () => {
      close();
    };

    const openFilterGroup = (i: number) => {
      showGroup.value[i] = !showGroup.value[i];
    };

    const getGroupNameTranslation = ({ groupName, components }): string => {
      switch (groupName) {
        case 'orders-history':
          return t('network.filters.title.ordersHistory');
        case 'items-in-cart':
          if (components?.[0].type === WidgetTypes.ToggleWidget) {
            return t('network.filters.title.itemsInCart');
          }
          return null;
        case 'shop-details':
          return t('network.filters.title.shopDetails');
        case 'shop-information':
          return t('network.filters.title.shopInformation');
        case 'purchasing-benefit':
          return t('network.filters.title.purchasingBenefit');
        case 'discounts':
          if (components?.[0]?.type === WidgetTypes.ToggleWidget) {
            return t('network.filters.title.discounts');
          }
          return null;
        default:
          return '';
      }
    };

    return {
      open,
      close,
      opened,
      selectedFilters,
      setSelectedFilter,
      submitFilters,
      fullWidth,
      returnComponent,
      resolveWidgetComponent,
      WidgetTypes,
      selectedFiltersStateForAnalytics,
      getGroupedFilters,
      openFilterGroup,
      showGroup,
      getGroupNameTranslation,
    };
  },
});
</script>

<style lang="scss">
// AkDrawer gets `:z-index="1030"` in the template: 1030 stays under `.ak-modal`
// (1040) and the module's `.ak-toaster` (1031).
.filters-drawer {
  // Scoped to this drawer: a bare `.ak-drawer__content` here is global and
  // restyles every AkDrawer in the app once this chunk loads.
  .ak-drawer__content {
    @apply ds-flex ds-flex-col ds-h-screen ds-justify-between;
  }
}

.filters-drawer__main {
  @apply ds-p-6 ds-mb-auto;
}

.filters-drawer__section {
  @apply ds-pt-3 ds-mb-3;
}

.filters-drawer__bottom {
  @apply ds-text-right ds-p-5;
}

.filter-group-name {
  @apply ds-text-base #{!important};
}
</style>
